import { type FC, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { RANGE_PERIOD, type RangeKey } from '../userScoreRange'
import { SCORE_BENCHMARK } from '../scoreRoster'
import { type AreaToImprove } from '../userScoreResultAreas'
import { formatShortDateWithYear } from '../../../common/dateUtils'
import styles from './UserAreaToImproveRow.module.scss'

const MEETING_PREVIEW_COUNT = 5

interface AreaToImproveRowProps {
  area: AreaToImprove
  range: RangeKey
  userId: string
  canViewMeetings: boolean
}

export const UserAreaToImproveRow: FC<AreaToImproveRowProps> = ({ area, range, userId, canViewMeetings }) => {
  const [expanded, setExpanded] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const improving = area.trend === 'fewer'

  const toggleExpanded = () => setExpanded((prev) => !prev)

  useEffect(() => {
    if (expanded) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      rootRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'nearest' })
    }
  }, [expanded])

  return (
    <div ref={rootRef} className={classNames('d-flex flex-column zocks-gap-0.5', styles.areaRow, expanded ? styles.areaRowExpanded : null)}>
      <div
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        className={classNames('d-flex align-items-center gap-3', styles.areaRowHeader)}
        onClick={toggleExpanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggleExpanded()
          }
        }}
      >
        <div className='d-flex flex-column zocks-gap-0.5 flex-grow-1'>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              <span className='paragraph-medium bold zocks-text-gray-900'>{area.title}</span>
              <span className='lead-extra-small zocks-text-gray-700 zocks-bg-color-gray-200 rounded-1 zocks-px-0.25 zocks-py-0.125'>{area.category}</span>
            </div>
            <div className='paragraph-medium zocks-text-gray-700'>Try this: {area.tip}</div>
          </div>
          <div className='d-flex align-items-center gap-1 paragraph-small flex-wrap'>
            {area.trend === 'new' ? (
              <>
                <span className={classNames('d-flex align-items-center justify-content-center flex-shrink-0', styles.newDotContainer)}>
                  <span className={styles.newDot} />
                </span>
                <span className='bold zocks-text-primary'>New</span>
                <span className='zocks-text-gray-700'>to this list</span>
              </>
            ) : area.trend === 'steady' ? (
              <>
                <i className='bi bi-dash-lg zocks-text-gray-600' />
                <span className='bold zocks-text-gray-700'>No change</span>
                <span className='zocks-text-gray-700'>vs the previous {RANGE_PERIOD[range]}</span>
              </>
            ) : (
              <>
                <i className={classNames('bi', improving ? classNames('bi-graph-down-arrow', styles.deltaUp) : 'bi-graph-up-arrow zocks-text-danger-200')} />
                <span className={classNames('bold', styles.tabularNums, improving ? styles.deltaUp : 'zocks-text-danger-200')}>
                  {area.trendMagnitude} {area.trend}
                </span>
                <span className='zocks-text-gray-700'>than the previous {RANGE_PERIOD[range]}</span>
              </>
            )}
            <span className='zocks-text-gray-700'>∙</span>
            <span className='zocks-text-gray-700'>
              Flagged in {area.flaggedCount} of {area.totalMeetings} meetings
            </span>
          </div>
        </div>
        <div className='d-flex align-items-center gap-2 flex-shrink-0'>
          <span className={classNames('paragraph-small zocks-text-primary', styles.areaRowHoverLabel)}>{expanded ? 'Hide meetings' : 'View meetings'}</span>
          <i className={classNames('bi zocks-text-gray-600', expanded ? 'bi-chevron-up' : 'bi-chevron-down')} />
        </div>
      </div>
      {expanded ? (
        <div className={classNames('border zocks-border-color-gray-200 zocks-bg-color-white px-3 py-2 mt-2', styles.meetingListPanel, styles.panelEnter)}>
          <div className='d-flex flex-column'>
            {area.meetings.slice(0, MEETING_PREVIEW_COUNT).map((meeting) => (
              <div key={`${meeting.room_id}-${meeting.session_id}`} className={classNames('d-flex align-items-center gap-3 zocks-py-0.75', styles.meetingRow)}>
                <span className={classNames('paragraph-small bold zocks-text-gray-600 text-uppercase flex-shrink-0', styles.tabularNums, styles.meetingDate)}>
                  {formatShortDateWithYear(new Date(meeting.date))}
                </span>
                <span className={classNames('paragraph-small zocks-text-gray-900 flex-grow-1 mw-0 text-truncate', styles.meetingRowLink)}>{meeting.title}</span>
                <span
                  className={classNames(
                    'paragraph-small bold rounded-1 zocks-px-0.25 zocks-py-0.125 flex-shrink-0',
                    styles.tabularNums,
                    meeting.score >= SCORE_BENCHMARK
                      ? 'zocks-text-success-200 zocks-bg-color-success-background'
                      : 'zocks-text-gray-900 zocks-bg-color-warning',
                  )}
                >
                  {Math.round(meeting.score)}
                </span>
                {canViewMeetings ? (
                  <Link
                    to={`/workspace/past-meetings/room/${meeting.room_id}/session/${meeting.session_id}/`}
                    className={classNames('paragraph-small zocks-text-gray-900 text-decoration-none flex-shrink-0', styles.meetingRowLink)}
                    aria-label={`View meeting ${meeting.title}`}
                  >
                    →
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
          {canViewMeetings ? (
            <Link
              to={`/workspace/past-meetings?userId=${userId}&page=1&pageSize=50`}
              className='paragraph-small zocks-text-primary text-decoration-none d-inline-flex align-items-center gap-1 mt-2'
            >
              View all in Past meetings <span aria-hidden='true'>→</span>
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
